import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, inr } from "@/lib/api";
import { CustomerHeader, RatingStars } from "@/components/tabdine/Shared";
import { Button } from "@/components/ui/button";
import { Check, FileText, XCircle } from "lucide-react";
import { toast } from "sonner";

const STEPS = ["new", "confirmed", "preparing", "ready", "served"];

export default function OrderConfirm() {
  const [params] = useSearchParams();
  const id = params.get("order");
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [rated, setRated] = useState(false);

  useEffect(() => {
    if (!id) return;
    const load = () => api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [id]);

  const [cancelling, setCancelling] = useState(false);
  const canCancel = ["new", "confirmed", "preparing"].includes(order?.status);

  const cancelOrder = async () => {
    if (!window.confirm("Cancel this order? This can't be undone.")) return;
    setCancelling(true);
    try {
      await api.patch(`/orders/${id}/cancel`);
      const r = await api.get(`/orders/${id}`);
      setOrder(r.data);
      toast.success("Order cancelled");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel order");
    }
    setCancelling(false);
  };

  const submitRating = async (r) => {
    await api.post(`/orders/${id}/feedback`, { rating: r });
    setRated(true);
    if (r >= 4 && order?.google_review_url) {
      window.open(order.google_review_url, "_blank");
    } else {
      toast.success("Thanks for your feedback!");
    }
  };

  if (!order) return <div className="min-h-screen grid place-items-center">Loading…</div>;
  const stepIdx = STEPS.indexOf(order.status);
  const isCancelled = order.status === "cancelled";
  const isPaid = order.status === "paid" || order.payment_status === "paid";

  return (
    <div className="min-h-screen bg-brand-light">
      <CustomerHeader />
      <div className="max-w-lg mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl border border-brand-dark/5 p-5 text-center">
          {isCancelled ? (
            <>
              <div className="w-14 h-14 mx-auto rounded-full bg-red-100 text-red-600 grid place-items-center">
                <XCircle size={26} />
              </div>
              <h1 className="font-heading text-2xl font-bold mt-3 text-red-700" data-testid="cancelled-title">Order cancelled</h1>
              {order.cancellation_reason && (
                <div className="mt-2 inline-block text-xs bg-red-50 text-red-700 px-3 py-1.5 rounded-full" data-testid="cancellation-reason">
                  {order.cancellation_reason}
                </div>
              )}
              {order.cancelled_at && (
                <div className="text-xs text-brand-dark/50 mt-1">
                  {new Date(order.cancelled_at).toLocaleString("en-IN")}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="w-14 h-14 mx-auto rounded-full bg-green-100 text-green-600 grid place-items-center">
                <Check size={26} />
              </div>
              <h1 className="font-heading text-2xl font-bold mt-3">Order placed!</h1>
              <div className="text-brand-dark/60 text-sm mt-1">Your food is on the way.</div>
            </>
          )}
          <div className="mt-3 flex justify-center gap-6 text-sm">
            <div>
              <div className="text-xs text-brand-dark/50">Order</div>
              <div className="font-heading font-semibold" data-testid="order-no">{order.order_no}</div>
            </div>
            <div>
              <div className="text-xs text-brand-dark/50">KOT</div>
              <div className="font-heading font-semibold">{order.kot_number}</div>
            </div>
            {order.table_number && (
              <div>
                <div className="text-xs text-brand-dark/50">Table</div>
                <div className="font-heading font-semibold">#{order.table_number}</div>
              </div>
            )}
            <div>
              <div className="text-xs text-brand-dark/50">Type</div>
              <div className="font-heading font-semibold" data-testid="oc-order-type">{order.order_type === "takeaway" ? "Takeaway" : "Dine-In"}</div>
            </div>
          </div>
          {!isCancelled && <div className="mt-2 text-xs text-brand-dark/60">Ready in ~{order.estimated_time_minutes} min</div>}
        </div>

        {/* Status tracker (hidden when cancelled) */}
        {!isCancelled && (
        <div className="mt-4 bg-white rounded-2xl border border-brand-dark/5 p-4" data-testid="status-tracker">
          <div className="flex justify-between items-center relative">
            {STEPS.map((s, i) => (
              <div key={s} className="flex flex-col items-center flex-1 relative z-10">
                <div
                  className={`w-7 h-7 rounded-full grid place-items-center text-xs font-bold ${
                    i <= stepIdx ? "bg-brand-orange text-white" : "bg-brand-dark/10 text-brand-dark/50"
                  }`}
                >
                  {i + 1}
                </div>
                <div className={`text-[10px] mt-1 capitalize ${i <= stepIdx ? "text-brand-dark" : "text-brand-dark/40"}`}>
                  {s}
                </div>
              </div>
            ))}
            <div className="absolute top-3.5 left-3 right-3 h-0.5 bg-brand-dark/10 -z-0" />
            <div
              className="absolute top-3.5 left-3 h-0.5 bg-brand-orange transition-all -z-0"
              style={{ width: `${(stepIdx / (STEPS.length - 1)) * 100}%` }}
            />
          </div>
        </div>
        )}

        {/* Items */}
        <div className="mt-4 bg-white rounded-2xl border border-brand-dark/5 p-4">
          <div className="font-heading font-semibold text-sm mb-2">Your items</div>
          {order.items.map((it) => (
            <div key={it.id} className="flex justify-between text-sm py-0.5">
              <span>{it.quantity} × {it.item_name}</span>
              <span>{inr(it.line_total)}</span>
            </div>
          ))}
          <div className="border-t border-brand-dark/10 my-2" />
          <div className="flex justify-between font-heading font-semibold">
            <span>Total</span>
            <span>{inr(order.total_amount)}</span>
          </div>
        </div>

        {order.status === "served" && !rated && (
          <div className="mt-4 bg-white rounded-2xl border border-brand-dark/5 p-4 text-center" data-testid="rating-prompt">
            <div className="font-heading font-semibold">Rate your visit</div>
            <div className="flex justify-center gap-1 mt-2">
              {[1, 2, 3, 4, 5].map((r) => (
                <button
                  key={r}
                  onClick={() => submitRating(r)}
                  className="text-3xl text-brand-dark/20 hover:text-amber-500"
                  data-testid={`rate-${r}`}
                >
                  ★
                </button>
              ))}
            </div>
          </div>
        )}

        {isPaid && (
          <div className="mt-4 bg-white rounded-2xl border border-brand-dark/5 p-4 flex items-center justify-between" data-testid="invoice-cta">
            <div>
              <div className="font-heading font-semibold text-brand-dark">Invoice ready</div>
              <div className="text-xs text-brand-dark/60">Download or print your receipt.</div>
            </div>
            <Link
              to={`/invoice?order=${order.id}`}
              className="inline-flex items-center gap-1 bg-brand-orange text-white px-3 py-2 rounded-full text-sm hover:bg-brand-orange-dark"
              data-testid="view-invoice"
            >
              <FileText size={14} /> View invoice
            </Link>
          </div>
        )}

        {canCancel && (
          <div className="mt-4">
            <Button
              variant="outline"
              className="w-full border-red-300 text-red-600 hover:bg-red-50"
              onClick={cancelOrder}
              disabled={cancelling}
              data-testid="cancel-order"
            >
              {cancelling ? "Cancelling…" : "Cancel order"}
            </Button>
          </div>
        )}

        <div className="mt-4 flex gap-2">
          <Button variant="outline" className="flex-1" onClick={() => nav(`/?r=${order.restaurant_slug}`)} data-testid="add-more">
            Add more items
          </Button>
          <Button className="flex-1 bg-brand-dark text-brand-light" onClick={() => nav("/profile")} data-testid="my-orders">
            My orders
          </Button>
        </div>
      </div>
    </div>
  );
}
